/**
 * This file contains many functions that make calls to our API and return the promises from those calls.
 */
import axios from 'axios';
import { get, omit } from 'lodash';
import qs from 'qs';
import { eventChannel, END } from 'redux-saga';
import { toast } from 'react-toastify';

import { NotUndefined } from '@redux-saga/types';
import Api from '../api';
import { Vendor } from './vendor';

const FACTORY = 'factories';

export interface Factory {
  address: string;
  address_2: string;
  approved_at: string | null;
  approved_by: string | null;
  audit_expiration_date: string;
  city: string;
  contact_email: string;
  contact_name: string;
  contact_phone: string;
  country: string;
  createdAt: string;
  garment_registration_number: string;
  id: number;
  internal_id: number;
  legal_name: string;
  preferred: boolean;
  rejected_at: string | null;
  rejected_by: string | null;
  rejection_reason: string | null;
  state: string;
  submitted_at: string;
  updatedAt: string;
  vendor: Vendor;
  vendor_internal_id: string;
  zip: string;
}

function formatUrl(userId: string | null) {
  return userId ? `/users/${userId}/${FACTORY}` : `/${FACTORY}`;
}

interface Data {
  [key: string]: string;
}

async function uploadFactoryFile(
  data: Data,
  signedUrls: unknown,
  key: string,
  emitter: (input: END | NotUndefined) => void,
) {
  if (!get(data, key)) return Promise.resolve();

  return axios({
    method: 'put',
    url: get(signedUrls, `factory.${key}.write`),
    data: data[key],
    headers: {
      'Content-Type': 'application/pdf',
    },
    onUploadProgress: (progressEvent) => {
      emitter({ progressEvent, fileName: key });
    },
  })
    .then(async () => {
      toast('File successfully uploaded', { type: 'success' });
      emitter({ data: signedUrls });
      emitter(END);
    })
    .catch((error) => {
      console.log('File upload error: ', error);
      toast('Something went wrong in file upload', {
        type: 'error',
      });
      emitter(END);
    });
}

export function uploadOneFactoryFile(
  file: File,
  signedUrl: string,
  name: 'auditDocument' | 'factoryAgreement',
  emitter: (input: END | NotUndefined) => void,
) {
  if (!file) return Promise.resolve();

  return axios({
    method: 'put',
    url: signedUrl,
    data: file,
    headers: {
      'Content-Type': 'application/pdf',
    },
    onUploadProgress: (progressEvent) => {
      emitter({ progressEvent, fileName: name });
    },
  })
    .then(async () => {
      toast('File successfully uploaded', { type: 'success' });
      emitter({ data: signedUrl });
      emitter(END);
    })
    .catch((error) => {
      console.log('File upload error: ', error);
      toast('Something went wrong in file upload', {
        type: 'error',
      });
      emitter(END);
    });
}

const VendorAPI = {
  async create(data: Data, userId = null) {
    const url = formatUrl(userId);
    const response = await Api.post(`${url}`, {
      data: omit(data, 'auditDocument', 'factoryAgreement'),
    });

    return eventChannel((emitter) => {
      uploadFactoryFile(data, response, 'auditDocument', emitter);
      uploadFactoryFile(data, response, 'factoryAgreement', emitter);
      return () => {};
    });
  },

  get(factoryId: string, userId = null) {
    const url = formatUrl(userId);
    return Api.get(`${url}/${factoryId}`);
  },

  async updateFile(
    file: File,
    signedUrl: string,
    fileName: 'auditDocument' | 'factoryAgreement',
  ) {
    return eventChannel((emitter) => {
      uploadOneFactoryFile(file, signedUrl, fileName, emitter);
      return () => {};
    });
  },

  async update(data: Data, factoryId: string, userId = null) {
    const url = formatUrl(userId);
    const response = await Api.put(`${url}/${factoryId}`, {
      data: omit(data, 'auditDocument', 'factoryAgreement'),
    });

    return response;
  },

  getAll(params: unknown, userId = null) {
    const url = formatUrl(userId);
    return Api.get(`${url}?${qs.stringify(params)}`);
  },

  delete(factoryId: string, userId = null) {
    const url = formatUrl(userId);
    return Api.delete(`${url}/${factoryId}`);
  },
};

export default VendorAPI;
